import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { channelAPI, conversationAPI, notificationAPI } from '../lib/api';
import { useWorkspace } from './WorkspaceContext';

const NotificationContext = createContext(null);

export const useNotifications = () => {
    const context = useContext(NotificationContext);
    if (!context) {
        throw new Error('useNotifications must be used within a NotificationProvider');
    }
    return context;
};

export const NotificationProvider = ({ children }) => {
    const { currentWorkspace } = useWorkspace();
    const [totalUnreadChannels, setTotalUnreadChannels] = useState(0);
    const [totalUnreadDMs, setTotalUnreadDMs] = useState(0);
    const [channelUnreadMap, setChannelUnreadMap] = useState({});
    const [dmUnreadMap, setDmUnreadMap] = useState({});
    const [loading, setLoading] = useState(false);
    const isFetchingRef = useRef(false);
    const pendingRefreshRef = useRef(false);

    // In-app notification center state
    const [notifications, setNotifications] = useState([]);
    const [unreadNotifCount, setUnreadNotifCount] = useState(0);
    const [notifLoading, setNotifLoading] = useState(false);
    const [notifHasMore, setNotifHasMore] = useState(false);
    const notifPollRef = useRef(null);

    const fetchTotals = useCallback(async (workspaceId) => {
        if (!workspaceId) return;
        if (isFetchingRef.current) {
            pendingRefreshRef.current = true;
            return;
        }
        isFetchingRef.current = true;
        setLoading(true);
        try {
            const [channelRes, conversationRes] = await Promise.all([
                channelAPI.getByWorkspace(workspaceId),
                conversationAPI.getByWorkspace(workspaceId),
            ]);

            const channels = channelRes.data.channels || [];
            const channelMap = {};
            const channelTotal = channels.reduce((sum, ch) => {
                channelMap[ch.id] = {
                    unread_count: ch.unread_count || 0,
                    unread_mention_count: ch.unread_mention_count || 0,
                    last_mention_preview: ch.last_mention_preview || null,
                    is_muted: ch.is_muted || false,
                };
                if (ch.is_muted) return sum;
                return sum + (ch.unread_count || 0);
            }, 0);

            const conversations = conversationRes.data.conversations || [];
            const dmMap = {};
            const dmTotal = conversations.reduce((sum, conv) => {
                dmMap[conv.id] = {
                    unread_count: conv.unread_count || 0,
                    is_muted: false,
                };
                return sum + (conv.unread_count || 0);
            }, 0);

            setChannelUnreadMap(channelMap);
            setDmUnreadMap(dmMap);
            setTotalUnreadChannels(channelTotal);
            setTotalUnreadDMs(dmTotal);
        } catch (error) {
            console.error('Failed to load notification totals:', error);
        } finally {
            setLoading(false);
            isFetchingRef.current = false;
            if (pendingRefreshRef.current) {
                pendingRefreshRef.current = false;
                fetchTotals(workspaceId);
            }
        }
    }, []);

    // Refetch when workspace changes
    useEffect(() => {
        if (currentWorkspace?.id) {
            fetchTotals(currentWorkspace.id);
        } else {
            setTotalUnreadChannels(0);
            setTotalUnreadDMs(0);
            setChannelUnreadMap({});
            setDmUnreadMap({});
        }
    }, [currentWorkspace?.id, fetchTotals]);

    const fetchNotifications = useCallback(async (workspaceId, reset = true) => {
        if (!workspaceId) return;
        setNotifLoading(true);
        try {
            const skip = reset ? 0 : notifications.length;
            const res = await notificationAPI.getList(workspaceId, skip, 20);
            const list = res.data?.notifications || [];
            setNotifications(prev => reset ? list : [...prev, ...list]);
            setNotifHasMore(list.length === 20);
        } catch (err) {
            console.error('Failed to fetch notifications:', err);
        } finally {
            setNotifLoading(false);
        }
    }, [notifications.length]);

    const refreshUnreadCount = useCallback(async (workspaceId) => {
        if (!workspaceId) return;
        try {
            const res = await notificationAPI.getUnreadCount(workspaceId);
            setUnreadNotifCount(res.data?.count || 0);
        } catch {}
    }, []);

    const markNotifRead = useCallback(async (id) => {
        try {
            await notificationAPI.markRead(id);
            setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
            setUnreadNotifCount(prev => Math.max(0, prev - 1));
        } catch (err) {
            console.error('Failed to mark notification read:', err);
        }
    }, []);

    const markAllNotifRead = useCallback(async () => {
        if (!currentWorkspace?.id) return;
        try {
            await notificationAPI.markAllRead(currentWorkspace.id);
            setNotifications(prev => prev.map(n => ({ ...n, read: true })));
            setUnreadNotifCount(0);
        } catch (err) {
            console.error('Failed to mark all notifications read:', err);
        }
    }, [currentWorkspace?.id]);

    const deleteNotif = useCallback(async (id) => {
        try {
            await notificationAPI.deleteOne(id);
            setNotifications(prev => {
                const notif = prev.find(n => n.id === id);
                if (notif && !notif.read) setUnreadNotifCount(c => Math.max(0, c - 1));
                return prev.filter(n => n.id !== id);
            });
        } catch (err) {
            console.error('Failed to delete notification:', err);
        }
    }, []);

    // Poll unread count every 30s
    useEffect(() => {
        if (!currentWorkspace?.id) return;
        refreshUnreadCount(currentWorkspace.id);
        notifPollRef.current = setInterval(() => refreshUnreadCount(currentWorkspace.id), 30000);
        return () => clearInterval(notifPollRef.current);
    }, [currentWorkspace?.id, refreshUnreadCount]);

    const markChannelRead = useCallback((channelId) => {
        setChannelUnreadMap(prev => {
            const current = prev[channelId];
            if (!current) return prev;
            const wasUnread = current.is_muted ? 0 : (current.unread_count || 0);
            if (wasUnread === 0 && current.unread_mention_count === 0) return prev;

            const next = {
                ...prev,
                [channelId]: {
                    ...current,
                    unread_count: 0,
                    unread_mention_count: 0,
                    last_mention_preview: null,
                },
            };

            setTotalUnreadChannels(total => Math.max(0, total - wasUnread));
            return next;
        });
    }, []);

    const markDMRead = useCallback((conversationId) => {
        setDmUnreadMap(prev => {
            const current = prev[conversationId];
            if (!current || current.unread_count === 0) return prev;

            const next = {
                ...prev,
                [conversationId]: {
                    ...current,
                    unread_count: 0,
                },
            };

            setTotalUnreadDMs(total => Math.max(0, total - (current.unread_count || 0)));
            return next;
        });
    }, []);

    const incrementChannelUnread = useCallback((channelId, increment = 1, mention = false) => {
        setChannelUnreadMap(prev => {
            const current = prev[channelId] || { unread_count: 0, unread_mention_count: 0, last_mention_preview: null, is_muted: false };
            if (current.is_muted) return prev;

            const next = {
                ...prev,
                [channelId]: {
                    ...current,
                    unread_count: (current.unread_count || 0) + (mention ? 0 : increment),
                    unread_mention_count: (current.unread_mention_count || 0) + (mention ? increment : 0),
                },
            };
            return next;
        });
        setTotalUnreadChannels(total => total + (mention ? increment : increment));
    }, []);

    const value = {
        totalUnreadChannels,
        totalUnreadDMs,
        channelUnreadMap,
        dmUnreadMap,
        loading,
        refresh: () => {
            if (currentWorkspace?.id) fetchTotals(currentWorkspace.id);
        },
        markChannelRead,
        markDMRead,
        incrementChannelUnread,
        // In-app notification center
        notifications,
        unreadNotifCount,
        notifLoading,
        notifHasMore,
        fetchNotifications: (reset) => fetchNotifications(currentWorkspace?.id, reset),
        markNotifRead,
        markAllNotifRead,
        deleteNotif,
        refreshUnreadCount: () => refreshUnreadCount(currentWorkspace?.id),
    };

    return (
        <NotificationContext.Provider value={value}>
            {children}
        </NotificationContext.Provider>
    );
};
